/* eslint-disable */
import React, { useEffect, useState } from 'react'

import {
  TextboxSubmit,
  TextboxSearch,
  TextboxSearchInputType,
  DropdownSubmit
} from '@splunk-iteng/splunk-shared-components'
import dayjs from 'dayjs'

import {
  useChangeUserAttributeMutation,
  useChangeUserStatusMutation,
  useOktaUserQuery
} from '../../graphql/graphql'
import { useAccessLevel } from '../../context/AccessLevelContext'
import {
  DEFAULT_CLASS_NAME,
  DEFAULT_DROPDOWN_CLASS_NAME,
  OKTA_PROFILE_ATTRIBUTES,
  PROFILE_READ_ONLY_ROLES
} from '../../constants/constants'
import { Link } from 'react-router-dom'

import { formatLocalTimezone } from '../../utils'

export function OktaProfileComponent() {
  const [profile, setUserProfile] = useState({} as any)
  const [isError, setIsError] = useState(false)
  const [email, setEmail] = useState('')
  const [activeIndex, setActiveIndex] = useState('')
  const { error, data, loading, refetch } = useOktaUserQuery({
    variables: { username: email },
    skip: !email
  })
  const [changeUserAttributeMutation, changeUserAttributeMutationResult] =
    useChangeUserAttributeMutation()
  const [changeUserStatusMutation] = useChangeUserStatusMutation()
  const setProfile = (p: any) => {
    const passwordChanged = dayjs(p.passwordChanged).format(
      'YYYY-MM-DD HH:mm:ss'
    )

    setUserProfile({ ...p, passwordChanged })
  }
  const { userRoles } = useAccessLevel()
  const isReadOnly = userRoles.some(role => PROFILE_READ_ONLY_ROLES.includes(role))

  useEffect(() => {
    setProfile(data ? data.oktaUserProfile : {})
  }, [data])

  useEffect(() => {
    if (error) alert(error.message)
  }, [error])

  const onShow = (name: string) => {
    setActiveIndex(name)
  }
  const handleSearchSubmit = (e: string) => {
    setEmail(e)

    if (e.length !== 0) {
      refetch()
    }
  }

  const handleProfileStatus = async () => {
    if (!profile || !profile.id) {
      return
    }

    await changeUserStatusMutation({
      variables: {
        id: profile.id,
        newStatus: profile.status === 'DEPROVISIONED' ? 'ACTIVE' : 'DEPROVISIONED'
      },
      onCompleted: () => {
        refetch()
      },
      onError: (err) => {
        const message = err.message.search('Must be in email format')
        if (message >= 0) {
          alert('Please provide a value with a valid email format')
        } else {
          alert(err.message)
        }
        refetch()
      }
    })
  }

  const saveAttribute = async (
    id: string,
    attribute: string,
    value: string
  ) => {
    await changeUserAttributeMutation({
      variables: {
        id,
        attribute,
        value
      }
    })
    setIsError(false)
  }

  const submitSingleField = async (name: string, value: string) => {
    if (!profile.id) {
      return
    }
    try {
      await saveAttribute(profile.id, name, value)
      await refetch()
    } catch (err) {
      let msg = err.message
      if (msg.includes('Does not match required pattern')) {
        msg = "Enter a valid email address"
      }
      alert(msg)
      throw err;
    }
  }

  const formSubmit = (e: any) => {
    e.preventDefault()
  }

  const renderLogAccess = (email: string) => {
    const now = new Date()
    const endTime = now.toISOString()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(now.getDate() - 30)
    const startTime = thirtyDaysAgo.toISOString()

    return (
      <div className="p-20">
        <Link
          className="btn btn-primary"
          to={{
            pathname: '/profile/logs',
            search: `?email=${encodeURIComponent(
              email
            )}&startTime=${encodeURIComponent(
              startTime
            )}&endTime=${encodeURIComponent(endTime)}`
          }}
          target="_blank"
        >
          <span>View Logs</span>
        </Link>
      </div>
    )
  }
  
  const renderPIIView = (email: string, readOnly: boolean) => {
    if (readOnly) {
      return null
    }

    return (
      <div className="p-20">
        <Link
          className="btn btn-primary"
          to={{
            pathname: '/profile/pii',
            search: `?email=${encodeURIComponent(
              email
            )}`
          }}
          target="_blank"
        >
          <span>View PII Data</span>
        </Link>
      </div>
    )
  }

  let message

  if (loading) {
    message = 'Loading...'
  } else if (!data) {
    message = 'Please type an email to search'
  } else if (data && !data.oktaUserProfile) {
    message = 'No profile found'
  } else if (isError) {
    message = 'There was an error during the search'
  }

  return (
    <div className="row">
      <div className="col-12 pb-10">
        <TextboxSearch
          name="email"
          className={DEFAULT_CLASS_NAME}
          onSubmit={handleSearchSubmit}
          placeholder="Search profile"
          trimOnSubmit={true}
          inputType={TextboxSearchInputType.EMAIL}
        />
      </div>
      <div className="col-12 pb-50">
        {message ? (
          <div className="p-20">{message}</div>
        ) : (
          <div className="okta-profile pt-20">
            <div className="pb-20">
              <h1 className="text-2xl font-bold">User Profile Details</h1>
            </div>
            <form className="okta-profile" onSubmit={formSubmit}>
              <div>
                <h2>
                  Status:&nbsp;
                  <strong>{profile.status === 'DEPROVISIONED' ? 'DEACTIVATED' : profile.status}</strong>
                </h2>
                <h3>Okta IDP ID: <strong>{profile.id}</strong></h3>
                <h3>Registered: {formatLocalTimezone(profile.created)}</h3>
                <h3>Activated: {formatLocalTimezone(profile.activated)}</h3>
                <h3>Last Updated: {formatLocalTimezone(profile.lastUpdated)}</h3>
                <h3>Password Changed: {formatLocalTimezone(profile.passwordChanged)}</h3>
                <h3>Last Login: {formatLocalTimezone(profile.lastLogin)}</h3>
                <h3>Terms Accepted Time: {formatLocalTimezone(profile.termsAcceptedTime)}</h3>
              </div>
              <div className="flex items-center">
                <div className="p-20">
                  {!isReadOnly && (
                    <button
                      onClick={handleProfileStatus}
                      type="button"
                      className="btn btn-primary"
                    >
                      {profile.status === 'DEPROVISIONED' ? 'Activate' : 'Deactivate'}
                    </button>
                  )}
                </div>
                {renderLogAccess(email)}
                {renderPIIView(email, isReadOnly)}
              </div>
              <div className="pt-20">
                {OKTA_PROFILE_ATTRIBUTES.map((attribute) =>
                  attribute.type === 'dropdown' ? (
                    <DropdownSubmit
                      key={attribute.name}
                      label={attribute.label}
                      name={attribute.name} 
                      className={DEFAULT_DROPDOWN_CLASS_NAME}
                      style={{
                        width: '80%'
                      }}
                      value={profile[attribute.name] || ''}
                      options={attribute.options}
                      onSubmit={submitSingleField}
                      isActive={activeIndex === attribute.name}
                      readOnly={isReadOnly || activeIndex !== attribute.name && activeIndex !== ''}
                      onShow={onShow}
                    />
                  ) : (
                    <TextboxSubmit
                      key={attribute.name}
                      label={attribute.label}
                      name={attribute.name}
                      type={attribute.type}
                      className={DEFAULT_CLASS_NAME}
                      value={profile[attribute.name] || ''}
                      onSubmit={submitSingleField}
                      isActive={activeIndex === attribute.name}
                      readOnly={isReadOnly || activeIndex !== attribute.name && activeIndex !== ''}
                      onShow={onShow}
                    />
                  )
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
